import React from 'react';
import ReactDOM from 'react-dom';
import { useQuery } from 'urql';
import FocusTrap from 'focus-trap-react';
import {
    AccessibilityVariantsFragment,
    TypeVariantFragment,
    VideoModalQuery,
} from 'app/graphql/__generated__/types/contento';
import CONTENTO_TYPES from '../../domain/ContentoTypes';
import { isBrowser } from '../../utils/environment';
import { createImageProxyUrl } from '../../utils/svtimage';
import VideoPlayerLazy from '../VideoPlayer/VideoPlayerLazy';
import VIDEO_MODAL_QUERY from './VideoModal.graphql';
import VideoModalError from './VideoModalError';
import { Container, SlashAnimationWrapper, SlashAnimation } from './VideoModal.styles';

const isVariant = (item: any): item is TypeVariantFragment => item.__typename === 'Variant';

const VideoModalPlay = ({ data }: { data: VideoModalQuery }) => {
    if (!data || !data.detailsPage) {
        return null;
    }

    const {
        detailsPage: {
            item: {
                barn,
                restrictions: { blockedForChildren },
                parent,
                name: episodeTitle,
                __typename,
                tags,
                urls: { svtplay: url },
            },
            svtId,
            images,
            item,
        },
        associatedContent,
    } = data;

    const nextVideo = data?.playQueue?.nextToPlay;
    const isLive = data?.detailsPage.live?.liveNow;

    const typeName =
        // eslint-disable-next-line no-nested-ternary
        __typename === CONTENTO_TYPES.VARIANT || __typename === CONTENTO_TYPES.CLIP
            ? parent
                ? parent.__typename
                : __typename
            : __typename;

    const posterImage = createImageProxyUrl(images.wide.id, images.wide.changed, 'large', 1024);

    const isKidsGenre = tags.some((tag) => tag.slug === 'barn' || tag.slug === 'barn-oppet-arkiv');

    return (
        <VideoPlayerLazy
            barn={barn}
            titleArticleId={parent ? parent.svtId : null}
            title={parent ? parent.name : episodeTitle}
            episodeTitle={episodeTitle}
            poster={posterImage}
            autoPlay
            blockedForChildren={blockedForChildren}
            svtId={svtId}
            __typename={typeName}
            __videoType={__typename}
            isKidsGenre={isKidsGenre}
            highlights={isVariant(item) ? item.highlights : null}
            url={url}
            videos={isVariant(item) ? (item.videos as Array<AccessibilityVariantsFragment>) : null}
            parentUrl={parent.urls.svtplay}
            associatedContent={associatedContent?.selections || []}
            nextVideo={nextVideo}
            whenToDisplaySkipToNext={isVariant(item) && item.whenToDisplaySkipToNext}
            isLive={isLive}
        />
    );
};

const VideoModal = ({ id }: { id: string }) => {
    const [result] = useQuery({
        query: VIDEO_MODAL_QUERY,
        variables: {
            id,
        },
    });

    const { data, error, fetching } = result;

    // Video player break if no parent is present
    const hasData = Boolean(data?.detailsPage?.item?.parent);
    const isError = error || (!hasData && !fetching);
    const shouldRender = isError || hasData; // FocusTrap can only render if there is something focusable

    return ReactDOM.createPortal(
        <>
            <SlashAnimationWrapper>
                <SlashAnimation version="1.1" x="0px" y="0px" viewBox="0 0 16 16">
                    <polygon points="4.368,14.056 8.884,1.945 11.632,1.945 7.116,14.056 " />
                </SlashAnimation>
            </SlashAnimationWrapper>
            {shouldRender && (
                <main role="main">
                    <Container role="dialog" aria-modal="true" aria-label="video modal">
                        {hasData && <VideoModalPlay data={data} />}
                        {isError && (
                            <FocusTrap>
                                <VideoModalError error={error} />
                            </FocusTrap>
                        )}
                    </Container>
                </main>
            )}
        </>,
        document.getElementById('__next')
    );
};

// Component split in two allow early return before useQuery hook.
const VideoModalContainer = ({ id }: { id: string }) => {
    if (!id || !isBrowser()) {
        return null;
    }

    return <VideoModal id={id} />;
};

export default VideoModalContainer;
